import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { usePlanFilters } from "@/components/list/planFilters";
import { activeFilters, ListActions, ListPrint, clamp2 } from "@/components/list/ListView";
import { QrFilterBar } from "@/components/quality/QrFilterBar";
import { PSummary, PSection, PTable } from "@/components/print/PrintDoc";
import { pl } from "@/components/quality/QrTools";
import { useAuth } from "@/context/AuthContext";
import { daDate, toDate, toISO, addDays, diffDays, isoWeek, monthLabel, quarterLabel, startOfMonday } from "@/lib/date";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CalendarRange, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw,
  ChevronDown, AlertTriangle, Layers, ExternalLink, Pencil, Loader2, SlidersHorizontal,
  Building2, Clock, Download, ArrowRight, GitBranch,
} from "lucide-react";

const TASK_STATUSES = ["Ikke startet", "I gang", "Afventer", "Færdig"];
const TYPE_LABEL = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
const CASE_STATUS_LABEL = { Ny: "Ny", "I gang": "I gang", "Afventer svar": "Afventer svar", "Færdig": "Færdig" };
const SCALES = [["dag", "Dag"], ["uge", "Uge"], ["måned", "Måned"], ["kvartal", "Kvartal"]];
const PX = { dag: 42, uge: 20, "måned": 7, kvartal: 3.4 };

const BAR = { "Ikke startet": "bg-stone-300", "I gang": "bg-[#A08979]", "Afventer": "bg-[#C9A83F]", "Færdig": "bg-[#7E856C]" };
const DOT = { "Ikke startet": "bg-stone-400", "I gang": "bg-[#A08979]", "Afventer": "bg-[#C9A83F]", "Færdig": "bg-[#7E856C]", "Forsinket": "bg-[#A83232]" };
const STATUS_BADGE = {
  "Ikke startet": "bg-stone-50 text-stone-500 border-stone-200",
  "I gang": "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]",
  "Afventer": "bg-[#F9F1D9] text-[#8A6E17] border-[#ECE0B6]",
  "Færdig": "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]",
};

const LEFT = 300;

function buildTicks(from, to, scale, ppd) {
  const start = toDate(from), end = toDate(to);
  if (!start || !end) return [];
  const ticks = [];
  const push = (d, w, label) => ticks.push({ left: diffDays(d, start) * ppd, w: w * ppd, label });
  if (scale === "dag") {
    let d = new Date(start);
    while (d <= end) { push(d, 1, `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`); d = addDays(d, 1); }
  } else if (scale === "uge") {
    let d = startOfMonday(start);
    while (d <= end) { push(d, 7, `Uge ${isoWeek(d)}`); d = addDays(d, 7); }
  } else if (scale === "måned") {
    let d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) { const n = new Date(d.getFullYear(), d.getMonth() + 1, 1); push(d, Math.round((n - d) / 86400000), monthLabel(d)); d = n; }
  } else {
    let d = new Date(start.getFullYear(), Math.floor(start.getMonth() / 3) * 3, 1);
    while (d <= end) { const n = new Date(d.getFullYear(), d.getMonth() + 3, 1); push(d, Math.round((n - d) / 86400000), quarterLabel(d)); d = n; }
  }
  return ticks;
}

function peakInWindow(spans, days) {
  if (!spans.length) return 0;
  let best = 0;
  const anchors = [];
  for (const [s, e] of spans) { anchors.push(s); anchors.push(addDays(e, -(days - 1))); }
  for (const a of anchors) {
    const winEnd = addDays(a, days - 1);
    let c = 0;
    for (const [s2, e2] of spans) if (s2 <= winEnd && e2 >= a) c++;
    best = Math.max(best, c);
  }
  return best;
}
function loadLevel(tasks) {
  const spans = tasks.filter((t) => t.has_period && t.status !== "Færdig").map((t) => [toDate(t.start_date), toDate(t.expected_end_date)]);
  const week = peakInWindow(spans, 7);
  if (week >= 9) return { label: "Overbelastet", cls: "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" };
  if (week >= 5) return { label: "Høj belastning", cls: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]" };
  return { label: "Normal", cls: "bg-[#E7EADF] text-[#7E856C] border-[#D3D8C6]" };
}
function hasOverlap(task, siblings) {
  if (!task.has_period) return false;
  const s = toDate(task.start_date), e = toDate(task.expected_end_date);
  return siblings.some((o) => o.id !== task.id && o.has_period && toDate(o.start_date) <= e && s <= toDate(o.expected_end_date));
}
const respIds = (t) => (t.responsible_user_ids && t.responsible_user_ids.length ? t.responsible_user_ids : (t.responsible_user_id ? [t.responsible_user_id] : []));

export default function CasePlan({ type }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState("uge");
  const [zoom, setZoom] = useState(1);
  const [collapsed, setCollapsed] = useState({});
  const [showAllUnscheduled, setShowAllUnscheduled] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [editing, setEditing] = useState(null);
    const scrollRef = useRef(null);

  const today = toISO(new Date());
  const defFrom = toISO(addDays(new Date(), -30));
  const defTo = toISO(addDays(new Date(), 180));
  const [range, setRange] = useState(null);
  const pf = usePlanFilters(`cp_filters_${type}`, { employee: "employee", caseId: "case", responsible: "responsible", customer: "customer", caseStatus: "cstatus", taskStatus: "tstatus" }, { from: range?.from || defFrom, to: range?.to || defTo, caseStatus: "open" });
  const f = pf.f; const setF = pf.setF;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [api.get(`/plan-view/case?type=${type}&${pf.query}`), api.get("/users").catch(() => ({ data: [] }))];
      const [cp, us] = await Promise.all(reqs);
      setData(cp.data);
      setUsers(us ? us.data.filter((u) => u.active !== false) : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke hente planlægningen");
    } finally { setLoading(false); }
  }, [type, pf.query]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const allTasks = data?.rows || data?.tasks || [];
  const assignees = data?.assignees || [];
  const unscheduled = data?.unscheduled || [];
  const canEditDept = data?.can_edit;
  const deptUsers = useMemo(() => (users || []).filter((u) => u.active !== false && (u.role === "admin" || (u.departments || []).includes(type))), [users, type]);

  const caseOpts = useMemo(() => [...new Map(allTasks.map((t) => [t.case_id, t.case_title])).entries()], [allTasks]);
  const responsibleOpts = useMemo(() => [...new Set(allTasks.map((t) => t.responsible_name).filter(Boolean))], [allTasks]);
  const customerOpts = useMemo(() => [...new Set(allTasks.map((t) => t.customer_name).filter(Boolean))], [allTasks]);

  const filtered = allTasks; // filtreret på serveren (plan-view)

  const withPeriod = filtered.filter((t) => t.has_period && t.in_window);
  const noPeriod = allTasks.filter((t) => !t.has_period);

  const groups = useMemo(() => {
    const nameMap = new Map(assignees.map((a) => [a.id, a.name]));
    const map = new Map();
    const addTo = (key, name, t) => {
      if (!map.has(key)) map.set(key, { key, name, tasks: [] });
      map.get(key).tasks.push(t);
    };
    for (const t of withPeriod) {
      const rids = respIds(t);
      if (f.employee !== "all") {
        if (rids.includes(f.employee)) addTo(f.employee, nameMap.get(f.employee) || t.responsible_user_name || "—", t);
        continue;
      }
      if (!rids.length) { addTo("__none__", "Ingen ansvarlig", t); continue; }
      for (const rid of rids) addTo(rid, nameMap.get(rid) || t.responsible_user_name || "—", t);
    }
    const arr = [...map.values()];
    for (const g of arr) {
      g.tasks.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      g.active = g.tasks.filter((t) => t.status !== "Færdig").length;
      g.delayed = g.tasks.filter((t) => t.is_delayed).length;
      g.load = loadLevel(g.tasks);
      const pmap = new Map();
      for (const t of g.tasks) {
        if (!pmap.has(t.case_id)) pmap.set(t.case_id, { case_id: t.case_id, case_title: t.case_title, tasks: [] });
        pmap.get(t.case_id).tasks.push(t);
      }
      g.cases = [...pmap.values()];
      for (const p of g.cases) {
        p.tasks.sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        p.start = p.tasks.reduce((m, t) => (t.start_date && (!m || t.start_date < m) ? t.start_date : m), null);
        p.end = p.tasks.reduce((m, t) => (t.expected_end_date && (!m || t.expected_end_date > m) ? t.expected_end_date : m), null);
        p.anyDelayed = p.tasks.some((t) => t.is_delayed);
        p.active = p.tasks.filter((t) => t.status !== "Færdig").length;
      }
      g.cases.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    }
    const loadRank = { Normal: 0, "Høj belastning": 1, Overbelastet: 2 };
    arr.sort((a, b) => {
      if (sortBy === "active") return b.active - a.active;
      if (sortBy === "delayed") return b.delayed - a.delayed;
      if (sortBy === "load") return loadRank[b.load.label] - loadRank[a.load.label];
      return a.name.localeCompare(b.name, "da");
    });
    return arr;
  }, [withPeriod, sortBy, assignees, f.employee]);

  const ppd = PX[scale] * zoom;
  const ticks = useMemo(() => buildTicks(f.from, f.to, scale, ppd), [f.from, f.to, scale, ppd]);
  const totalW = Math.max(diffDays(toDate(f.to), toDate(f.from)) * ppd, 200);
  const todayLeft = diffDays(toDate(today), toDate(f.from)) * ppd;
  const scrolledToToday = useRef(false);
  useEffect(() => {
    if (scrolledToToday.current || loading || !rangeInit.current || !scrollRef.current) return;
    scrolledToToday.current = true;
    scrollRef.current.scrollLeft = Math.max(0, todayLeft - ppd);
  }, [todayLeft, loading, ppd]);
  const todayVisible = today >= f.from && today <= f.to;

  const shiftPeriod = (dir) => {
    const len = diffDays(toDate(f.to), toDate(f.from));
    setF((p) => ({ ...p, from: toISO(addDays(toDate(p.from), dir * len)), to: toISO(addDays(toDate(p.to), dir * len)) }));
  };
  const centerToday = () => {
    const len = diffDays(toDate(f.to), toDate(f.from));
    setF((p) => ({ ...p, from: toISO(addDays(new Date(), -Math.round(len / 2))), to: toISO(addDays(new Date(), Math.round(len / 2))) }));
  };
  const resetFilters = () => pf.reset();

  const exportView = () => api.get(`/plan-view/case/export.xlsx?type=${type}&${pf.query}`, { responseType: "blob" }).then((res) => {
    const m = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || ""); const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "plan.xlsx"; a.click(); URL.revokeObjectURL(url);
  }).catch(() => toast.error("Kunne ikke eksportere"));
  const printFilters = activeFilters({ params: pf.sp, getArr: pf.arr, opt: (k) => (data?.options?.[k] || []), period: { from: pf.sp.get("from") || "", to: pf.sp.get("to") || "" }, match: pf.match }, { employee: "Medarbejder", case: "Sag", responsible: "Sagsansvarlig", customer: "Kunde", cstatus: "Sagsstatus", tstatus: "Opgavestatus", vis: "Visning" });
  if (!pf.arr("cstatus").length) printFilters.unshift(["Sagsstatus", "Åbne"]);
  const counts = data?.counts || {};
  const nameOf = Object.fromEntries((data?.assignees || []).map((a) => [a.id, a.name]));
  const empSel = pf.arr("employee");
  const perPerson = withPeriod.flatMap((t) => ((t.responsible_user_ids || []).length ? t.responsible_user_ids : [null]).filter((r) => !empSel.length || empSel.includes(r)).map((r) => ({ ...t, person: r ? nameOf[r] || "Ingen ansvarlig" : "Ingen ansvarlig" })))
    .sort((a, b) => a.person.localeCompare(b.person, "da") || (a.start_date || "").localeCompare(b.start_date || ""));
  const exportExcel = async () => {
    try {
      const qs = f.employee !== "all" ? `&employee=${encodeURIComponent(f.employee)}` : "";
      const res = await api.get(`/case-plan/export/excel?type=${type}${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `planlaegning-${type}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Kunne ikke eksportere planlægningen"); }
  };

  const rangeInit = useRef(false);
  useEffect(() => {
    if (rangeInit.current) return;
    if (pf.sp.get("from") || pf.sp.get("to")) { rangeInit.current = true; return; } // gemt/delt periode vinder
    const periodTasks = allTasks.filter((t) => t.has_period && t.start_date);
    if (!periodTasks.length) return;
    rangeInit.current = true;
    let minStart = periodTasks[0].start_date;
    let maxEnd = periodTasks[0].expected_end_date || periodTasks[0].start_date;
    for (const t of periodTasks) {
      if (t.start_date < minStart) minStart = t.start_date;
      const e = t.expected_end_date || t.start_date;
      if (e > maxEnd) maxEnd = e;
    }
    const from = toISO(startOfMonday(addDays(toDate(minStart), -7)));
    const to = [toISO(addDays(toDate(maxEnd), 21)), toISO(addDays(new Date(), 90))].sort().pop();
    setRange({ from, to }); // kun lokalt vindue – ikke et filter i URL’en
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTasks]);

  const canEditTask = (t) => t.can_edit;

  if (loading && !data) {
    return <div className="flex items-center justify-center py-32 text-stone-400" data-testid="cp-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Henter planlægning…</div>;
  }

  return (
    <div className="space-y-6" data-testid="case-plan-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-[#1A1919]">Planlægning</h1>
          <p className="text-sm text-stone-500 mt-2 max-w-xl">Alle {TYPE_LABEL[type]?.toLowerCase()}-opgaver fordelt på ansvarlige i én tidslinje.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-[#EBE8E3] bg-white p-0.5 h-9 items-center" data-testid="cp-view-toggle">
            <button type="button" data-testid="cp-my-plan" onClick={() => setF((p) => ({ ...p, employee: user.id }))} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${f.employee === user?.id ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Min plan</button>
            <button type="button" data-testid="cp-all-employees" onClick={() => setF((p) => ({ ...p, employee: "all" }))} className={`px-3.5 h-full rounded-[10px] text-sm font-medium [transition-property:background-color,color] duration-200 ${f.employee === "all" ? "bg-[#A83232] text-white" : "text-stone-600 hover:text-stone-900"}`}>Alle medarbejdere</button>
          </div>
        </div>
      </div>

      <QrFilterBar hideQuick hideSaved placeholder="Søg sag, opgave, kunde, medarbejder…" search={pf.search} setSearch={(v) => pf.setParam({ q: v })} shown={withPeriod.length} total={data?.total ?? 0} onReset={resetFilters} match={pf.match} setMatch={(v) => pf.setParam({ match: v })}
        filters={[["employee", "Medarbejder"], ["case", "Sag"], ["responsible", "Sagsansvarlig"], ["customer", "Kunde"], ["cstatus", "Sagsstatus"], ["tstatus", "Opgavestatus"], ["vis", "Visning"]].map(([k, l]) => ({ key: k, label: l, options: data?.options?.[k] || [], selected: pf.arr(k), onChange: (v) => pf.setParam({ [k]: v }) }))}
        period={{ from: pf.sp.get("from") || "", to: pf.sp.get("to") || "", onChange: (fr, to) => setF({ from: fr || defFrom, to: to || defTo }) }} />
      <ListPrint kicker="BSM A/S · Planlægning" title="Sagsplanlægning" noun="opgaver" filters={printFilters}
        kpis={[{ label: "Opgaver i vinduet", value: withPeriod.length }, { label: "Forsinkede", value: counts.delayed || 0, dark: true }, { label: "Åbne", value: counts.open || 0 }, { label: "Medarbejdere", value: counts.people || 0 }]}
        summary={<PSummary>{pl(withPeriod.length, "opgave", "opgaver")} i perioden {daDate(f.from)} – {daDate(f.to)}, heraf {pl(counts.delayed || 0, "forsinket", "forsinkede")}. {pl(noPeriod.length, "opgave", "opgaver")} uden periode. Tabellen har én række pr. medarbejder pr. opgave (som på skærmen) – {pl(perPerson.length, "række", "rækker")}; nøgletallet tæller hvert opgave én gang.</PSummary>}
        extra={unscheduled.length > 0 && <PSection num="03" title="Sager uden tidsplan" note="Følger søgning, sag, sagsansvarlig, kunde og sagsstatus – ikke periode/medarbejder."><PTable cols={[{ label: "Sag", key: "t2" }, { label: "Kunde", key: "customer_name", w: "34mm" }, { label: "Sagsansvarlig", key: "r2", w: "30mm" }, { label: "Status", key: "status", w: "24mm", cls: "nowrap" }, { label: "Frist", key: "frist", w: "22mm", cls: "nowrap" }]} rows={unscheduled.map((c) => ({ ...c, t2: clamp2(c.title), r2: clamp2(c.responsible_name), frist: daDate(c.due_date) }))} /></PSection>}
        cols={[{ label: "Medarbejder", key: "person", w: "30mm" }, { label: "Sag", key: "case_title" }, { label: "Opgave", key: "task2", w: "42mm" }, { label: "Start", key: "start", w: "22mm", cls: "nowrap" }, { label: "Slut", key: "end", w: "22mm", cls: "nowrap" }, { label: "Status", key: "status", w: "24mm", cls: "nowrap" }]} rows={perPerson.map((t) => ({ ...t, start: daDate(t.start_date), end: daDate(t.expected_end_date), person: clamp2(t.person), task2: clamp2(t.task_name) }))} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1" data-testid="cp-scale">
          {SCALES.map(([v, l]) => (
            <button key={v} data-testid={`cp-scale-${v === "måned" ? "maaned" : v}`} onClick={() => setScale(v)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${scale === v ? "bg-[#1A1919] text-white" : "text-stone-500 hover:bg-[#F3F1EC]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="cp-prev" onClick={() => shiftPeriod(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="rounded-lg h-8" data-testid="cp-today" onClick={centerToday}>I dag</Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="cp-next" onClick={() => shiftPeriod(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-1 bg-white border border-[#EBE8E3] rounded-xl p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="cp-zoom-out" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><ZoomOut className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid="cp-zoom-in" onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}><ZoomIn className="h-4 w-4" /></Button>
          </div>
          <ListActions onExport={exportView} more={[]} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-stone-500">
        {["Ikke startet", "I gang", "Afventer", "Færdig", "Forsinket"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${DOT[s]}`} /> {s}</span>
        ))}
        <span className="inline-flex items-center gap-1.5 ml-2 text-stone-400"><span className="h-3 w-px bg-[#A83232]" /> I dag</span>
      </div>

      <div className="hidden md:block border border-[#EBE8E3] rounded-2xl bg-white overflow-hidden" data-testid="cp-gantt">
        <div className="overflow-x-auto" ref={scrollRef}>
          <div style={{ width: LEFT + totalW, minWidth: "100%" }}>
            <div className="flex sticky top-0 z-20 bg-[#FBFAF8] border-b border-[#EBE8E3]">
              <div className="sticky left-0 z-30 bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-stone-400" style={{ width: LEFT }}>Opgave</div>
              <div className="relative h-10" style={{ width: totalW }}>
                {ticks.map((t, i) => (
                  <div key={i} className="absolute top-0 h-full border-l border-[#F3F1EC] text-[11px] text-stone-500 pl-1.5 pt-2 whitespace-nowrap overflow-hidden" style={{ left: t.left, width: t.w }}>{t.label}</div>
                ))}
                {todayVisible && <div data-testid="cp-today-line-header" className="absolute top-0 h-full w-0.5 bg-[#A83232] z-10" style={{ left: todayLeft }} />}
              </div>
            </div>

            {groups.length === 0 && (
              <div className="px-4 py-16 text-center text-stone-400" data-testid="cp-empty">
                {allTasks.length === 0 ? "Ingen opgaver med tidsplan endnu. Tilføj varighed på opgaverne i en sag." : "Ingen opgaver matcher de valgte filtre."}
              </div>
            )}

            {groups.map((g) => (
              <div key={g.key}>
                <div className="flex bg-[#FBFAF8]/60 border-b border-[#F3F1EC]">
                  <div className="sticky left-0 z-10 bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-2.5 flex items-center gap-2" style={{ width: LEFT }}>
                    <button onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))} data-testid={`cp-group-toggle-${g.key}`} className="text-stone-400 hover:text-stone-700">
                      <ChevronDown className={`h-4 w-4 transition-transform ${collapsed[g.key] ? "-rotate-90" : ""}`} />
                    </button>
                    <span className="font-semibold text-sm text-stone-800 truncate" data-testid={`cp-group-name-${g.key}`}>{g.name}</span>
                    <span className="text-xs text-stone-400 shrink-0">· {g.active} aktive</span>
                    <Badge variant="outline" data-testid={`cp-load-${g.key}`} className={`ml-auto rounded-lg text-[11px] shrink-0 ${g.load.cls}`} title="Belastning pr. uge: Normal = 1–4 opgaver · Høj belastning = 5–8 · Overbelastet = 9 eller flere.">{g.load.label}</Badge>
                  </div>
                  <div className="relative bg-white" style={{ width: totalW }}>{todayVisible && <div className="absolute top-0 h-full w-0.5 bg-[#A83232]/40" style={{ left: todayLeft }} />}</div>
                </div>

                {!collapsed[g.key] && g.cases.map((p) => {
                  const barLeft = diffDays(toDate(p.start), toDate(f.from)) * ppd;
                  const barW = Math.max((diffDays(toDate(p.end), toDate(p.start)) + 1) * ppd, ppd);
                  return (
                    <div key={p.case_id} className="flex border-b border-[#F3F1EC] hover:bg-[#FBFAF8]/50 group" data-testid={`cp-case-row-${g.key}-${p.case_id}`}>
                      <div className="sticky left-0 z-10 bg-white group-hover:bg-[#FBFAF8] border-r border-[#EBE8E3] shrink-0 px-4 py-3 pl-9 flex items-center gap-2" style={{ width: LEFT }}>
                        <span className={`h-2 w-2 rounded-full shrink-0 ${p.anyDelayed ? DOT["Forsinket"] : "bg-stone-300"}`} />
                        <span className="text-sm font-medium text-stone-800 truncate" title={p.case_title}>{p.case_title}</span>
                        <span className="text-xs text-stone-400 shrink-0 ml-auto">{p.tasks.length} {p.tasks.length === 1 ? "opgave" : "opgaver"}</span>
                      </div>
                      <div className="relative py-3" style={{ width: totalW }}>
                        {ticks.map((tk, i) => <div key={i} className="absolute top-0 h-full border-l border-[#F3F1EC]" style={{ left: tk.left }} />)}
                        {todayVisible && <div className="absolute top-0 h-full w-0.5 bg-[#A83232]/40" style={{ left: todayLeft }} />}
                        <div data-testid={`cp-case-bar-${p.case_id}`} className="absolute rounded-lg bg-stone-100 ring-1 ring-stone-200/70 overflow-hidden shadow-sm" style={{ left: Math.max(barLeft, 0), width: barW, top: 8, height: 26 }}>
                          {p.tasks.map((seg) => {
                            const segLeft = diffDays(toDate(seg.start_date), toDate(p.start)) * ppd;
                            const segW = Math.max((diffDays(toDate(seg.expected_end_date), toDate(seg.start_date)) + 1) * ppd, 4);
                            return (
                              <Popover key={seg.id}>
                                <PopoverTrigger asChild>
                                  <button data-testid={`cp-segment-${seg.id}`}
                                    className={`absolute top-0 h-full ${seg.is_delayed ? "bg-[#A83232]" : BAR[seg.status]} hover:brightness-95 transition-all border-r border-white/70 last:border-r-0 flex items-center px-2`}
                                    style={{ left: Math.max(segLeft, 0), width: segW }} title={`${seg.task_name} · ${seg.status}`}>
                                    <span className="text-[10px] font-medium text-white/95 truncate">{seg.task_name}</span>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 rounded-2xl" data-testid={`cp-card-${seg.id}`}>
                                  <TaskCard t={seg} overlap={hasOverlap(seg, g.tasks)} canEdit={canEditTask(seg)} onEdit={() => setEditing(seg)} type={type} />
                                </PopoverContent>
                              </Popover>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="md:hidden space-y-3" data-testid="cp-mobile-list">
        {groups.length === 0 && <p className="text-center text-stone-400 py-10">{allTasks.length === 0 ? "Ingen opgaver med tidsplan endnu." : "Ingen opgaver matcher de valgte filtre."}</p>}
        {groups.map((g) => (
          <div key={g.key} className="bg-white border border-[#EBE8E3] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-semibold text-sm">{g.name}</span>
              <span className="text-xs text-stone-400">· {g.active} aktive</span>
              <Badge variant="outline" className={`ml-auto rounded-lg text-[11px] ${g.load.cls}`}>{g.load.label}</Badge>
            </div>
            <div className="space-y-2">
              {g.tasks.map((t) => (
                <div key={t.id} className="rounded-xl border border-[#F3F1EC] p-3" data-testid={`cp-mobile-task-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{t.case_title}</span>
                    <Badge variant="outline" className={`rounded-lg text-[11px] shrink-0 ${t.is_delayed ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : STATUS_BADGE[t.status]}`}>{t.is_delayed ? `Forsinket ${t.delay_days}d` : t.status}</Badge>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">{t.task_name}</p>
                  <p className="text-xs text-stone-400 mt-1">{daDate(t.start_date)} – {daDate(t.expected_end_date)}</p>
                  <Link to={`/sag/${t.case_id}`} className="text-xs text-[#A83232] font-medium inline-flex items-center gap-1 mt-2">Åbn sag <ExternalLink className="h-3 w-3" /></Link>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && <EditDialog task={editing} users={deptUsers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {unscheduled.length > 0 && (
        <div data-testid="cp-unscheduled">
          <div className="flex items-center justify-between mb-5">
            <h2 className="label-caps">Sager uden tidsplan</h2>
            <span className="text-xs font-medium text-stone-400">{unscheduled.length} {unscheduled.length === 1 ? "sag" : "sager"}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(showAllUnscheduled ? unscheduled : unscheduled.slice(0, 6)).map((p) => (
              <Link key={p.id} to={`/sag/${p.id}`} data-testid={`cp-unscheduled-${p.id}`}
                className="group rounded-2xl border border-[#EBE8E3] bg-white p-6 text-left hover:border-[#A83232]/50 hover:shadow-[0_4px_20px_rgba(0,0,0,0.05)] [transition-property:border-color,box-shadow] duration-200 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1A1919] truncate group-hover:text-[#A83232] [transition-property:color] duration-200">{p.title}</p>
                    <p className="text-sm text-stone-500 truncate mt-0.5">{p.customer_name || p.source_project_name || "—"}</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs text-stone-500 shrink-0 mt-1"><span className="h-1.5 w-1.5 rounded-full bg-stone-300" /> {CASE_STATUS_LABEL[p.status] || p.status}</span>
                </div>
                <div className="mt-5 pt-4 border-t border-[#F3F1EC] space-y-2.5 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-stone-400">Antal opgaver</span>
                    <span className="text-stone-700 font-medium">{p.task_total ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-stone-400">Deadline</span>
                    <span className="text-stone-500 text-right">{p.due_date ? daDate(p.due_date) : "Ikke fastlagt"}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#F3F1EC]">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-400 mb-1.5">Sagsansvarlig</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span className="h-6 w-6 rounded-full bg-[#A83232]/10 text-[#A83232] text-[10px] font-semibold flex items-center justify-center shrink-0">{(p.responsible_name || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "—"}</span>
                      <span className="text-xs text-stone-600 truncate">{p.responsible_name || "Ingen ansvarlig"}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-[#A83232] font-medium shrink-0 [transition-transform] duration-200 group-hover:translate-x-0.5">Åbn <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {unscheduled.length > 6 && (
            <div className="flex justify-center mt-5">
              <Button variant="outline" className="rounded-xl border-[#EBE8E3]" data-testid="cp-unscheduled-toggle" onClick={() => setShowAllUnscheduled((v) => !v)}>
                {showAllUnscheduled ? "Se færre" : `Se flere (${unscheduled.length - 6})`}
                <ChevronDown className={`h-4 w-4 ml-2 [transition-property:transform] duration-200 ${showAllUnscheduled ? "rotate-180" : ""}`} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-stone-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 rounded-xl bg-white" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function CheckToggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} data-testid={testid} />
      <span className="text-sm text-stone-600">{label}</span>
    </label>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 text-stone-400 shrink-0" strokeWidth={1.75} />
      <span className="text-stone-500 shrink-0">{label}</span>
      <span className="font-medium text-stone-800 text-right ml-auto truncate">{children}</span>
    </div>
  );
}

function TaskCard({ t, overlap, canEdit, onEdit, type }) {
  const responsibles = (t.responsible_user_name || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400 truncate">{t.customer_name || TYPE_LABEL[type]}</p>
          <p className="text-base font-semibold text-stone-900 leading-tight mt-1">{t.case_title}</p>
          <p className="text-sm text-stone-500 mt-0.5">{t.task_name}</p>
        </div>
        <Badge variant="outline" className={`rounded-full px-2.5 py-0.5 text-[11px] shrink-0 ${t.is_delayed ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : STATUS_BADGE[t.status]}`}>{t.is_delayed ? `Forsinket ${t.delay_days}d` : t.status}</Badge>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-stone-400">Fremdrift</span>
          <span className="font-semibold text-stone-700">{t.progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#F3F1EC] overflow-hidden">
          <div className={`h-full rounded-full [transition-property:width] duration-500 ${t.is_delayed ? "bg-[#A83232]" : t.progress === 100 ? "bg-[#7E856C]" : "bg-[#A83232]/70"}`} style={{ width: `${Math.max(t.progress, 3)}%` }} />
        </div>
      </div>

      <div>
        <p className="text-xs text-stone-400 mb-1.5">Ansvarlige</p>
        <div className="flex flex-wrap gap-1.5">
          {responsibles.length ? responsibles.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#EBE8E3] pl-1 pr-2.5 py-0.5 text-xs font-medium text-stone-700">
              <span className="h-5 w-5 rounded-full bg-[#A83232]/10 text-[#A83232] text-[10px] font-semibold flex items-center justify-center">{r.slice(0, 1).toUpperCase()}</span>
              {r}
            </span>
          )) : <span className="text-sm text-stone-400">Ingen ansvarlig</span>}
        </div>
      </div>

      <div className="rounded-xl bg-[#FBFAF8] border border-[#F3F1EC] p-3 space-y-2.5 text-sm">
        <InfoRow icon={CalendarRange} label="Periode">{daDate(t.start_date)} – {daDate(t.expected_end_date)}</InfoRow>
        <InfoRow icon={Clock} label="Varighed">{t.duration_days} dage</InfoRow>
        <InfoRow icon={Building2} label="Sagsansvarlig">{t.responsible_name || "—"}</InfoRow>
        <InfoRow icon={GitBranch} label="Kilde">{t.source_project_name ? `Projekt · ${t.source_project_name}` : "Ny sag"}</InfoRow>
      </div>

      {(t.is_delayed || overlap) && (
        <div className="space-y-1.5">
          {t.is_delayed && <p className="text-xs font-medium text-[#A83232] flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Forsinket med {t.delay_days} dage</p>}
          {overlap && <p className="text-xs text-[#806B61] flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 shrink-0" /> Flere opgaver i denne periode.</p>}
        </div>
      )}

      <div className="flex gap-2">
        <Link to={`/sag/${t.case_id}`} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-xl" data-testid={`cp-open-${t.id}`}><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Åbn sag</Button></Link>
        {canEdit && <Button size="sm" className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid={`cp-edit-${t.id}`} onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1.5" /> Redigér</Button>}
      </div>
    </div>
  );
}

function EditDialog({ task, users, onClose, onSaved }) {
  const initialResp = task.responsible_user_ids && task.responsible_user_ids.length ? task.responsible_user_ids : (task.responsible_user_id ? [task.responsible_user_id] : []);
  const [responsibles, setResponsibles] = useState(initialResp);
  const [status, setStatus] = useState(task.status);
  const [duration, setDuration] = useState(String(task.duration_days));
  const [saving, setSaving] = useState(false);

  const arrEq = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (!arrEq(responsibles, initialResp)) body.responsible_user_ids = responsibles;
      if (status !== task.status) body.status = status;
      if (Number(duration) !== task.duration_days) body.duration_days = Number(duration);
      if (Object.keys(body).length) await api.patch(`/cases/${task.case_id}/tasks/${task.task_id}`, body);
      toast.success("Opgaven er opdateret");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke gemme");
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="cp-edit-dialog">
        <DialogHeader>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">{task.customer_name || "Sag"}</p>
          <DialogTitle className="font-light text-2xl leading-tight">{task.case_title}</DialogTitle>
          <DialogDescription className="text-sm text-stone-500">{task.task_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-1">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Ansvarlige</Label>
            <ResponsibleMultiSelect users={users} contactName={null} value={responsibles} onChange={setResponsibles} testid="cp-edit-responsible" className="h-11" />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Status</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="cp-edit-status">
              {TASK_STATUSES.map((s) => {
                const active = status === s;
                return (
                  <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`cp-edit-status-${s}`}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium [transition-property:background-color,border-color,color] duration-200 ${active ? "border-[#A83232] bg-[#F1DEDA] text-[#A83232]" : "border-[#EBE8E3] bg-white text-stone-600 hover:border-stone-300"}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${DOT[s]}`} />
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Varighed</Label>
            <div className="relative">
              <Input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-xl h-11 pr-12" data-testid="cp-edit-duration" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-stone-400 pointer-events-none">dage</span>
            </div>
            <p className="text-xs text-stone-400">Opgavernes datoer beregnes ud fra sagens deadline og opgavernes varigheder.</p>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" className="rounded-xl" onClick={onClose} data-testid="cp-edit-cancel">Annullér</Button>
          <Button className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" onClick={save} disabled={saving} data-testid="cp-edit-save">{saving ? "Gemmer…" : "Gem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
